import React, { useState, useEffect } from 'react';
import { Calendar, FileText, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Info, Home } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const WFHApplication = () => {
  const [formData, setFormData] = useState({ startDate: '', endDate: '', reason: '' });
  const [wfhDays, setWfhDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wfhRequests, setWfhRequests] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('apply-wfh');
  const [previousRequests, setPreviousRequests] = useState([]);
  const [autoRefreshing, setAutoRefreshing] = useState(false);

  useEffect(() => {
    fetchWfhRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'my-wfh') {
      const interval = setInterval(() => {
        setAutoRefreshing(true);
        fetchWfhRequests().finally(() => setAutoRefreshing(false));
      }, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchWfhRequests = async () => {
    setListLoading(true);
    try {
      const response = await api.get('/employee/wfh');
      const newRequests = response.data.wfhRequests || [];

      if (previousRequests.length > 0) {
        newRequests.forEach(newReq => {
          const oldReq = previousRequests.find(old => old._id === newReq._id);
          if (oldReq && oldReq.status !== newReq.status) {
            const startDate = new Date(newReq.startDate).toLocaleDateString();
            const endDate = new Date(newReq.endDate).toLocaleDateString();
            const dateRange = startDate === endDate ? startDate : `${startDate} to ${endDate}`;
            if (newReq.status === 'approved') {
              toast.success(`Your WFH request for ${dateRange} has been approved!`);
            } else if (newReq.status === 'rejected') {
              toast.error(`Your WFH request for ${dateRange} has been rejected.`);
            }
          }
        });
      }

      setWfhRequests(newRequests);
      setPreviousRequests(newRequests);
    } catch (error) {
      console.error('Error fetching WFH requests:', error);
      toast.error('Failed to fetch WFH applications');
    } finally {
      setListLoading(false);
    }
  };

  const generateDatesArray = (start, end) => {
    const dates = [];
    let currentDate = new Date(start);
    const endDate = new Date(end);
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start <= end) {
        const dates = generateDatesArray(start, end);
        setWfhDays(dates.map(date => ({
          date: date.toISOString().split('T')[0],
          dayType: 'full'
        })));
      }
    } else {
      setWfhDays([]);
    }
  }, [formData.startDate, formData.endDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDayTypeChange = (index, dayType) => {
    const updated = [...wfhDays];
    updated[index].dayType = dayType;
    setWfhDays(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!formData.startDate || !formData.endDate) {
        toast.error('Please select start and end dates');
        setLoading(false);
        return;
      }
      if (!formData.reason || formData.reason.trim().length < 10) {
        toast.error('Please provide a reason with at least 10 characters');
        setLoading(false);
        return;
      }
      if (wfhDays.length === 0) {
        toast.error('Please select a valid date range');
        setLoading(false);
        return;
      }

      const submitData = {
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        wfhDays: wfhDays.map(day => ({
          date: new Date(day.date).toISOString(),
          dayType: day.dayType
        })),
        reason: formData.reason
      };

      await api.post('/employee/wfh', submitData);
      toast.success('WFH application submitted successfully!');
      setFormData({ startDate: '', endDate: '', reason: '' });
      setWfhDays([]);
      fetchWfhRequests();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to submit WFH application';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'pending': return <Clock className="h-5 w-5 text-yellow-600" />;
      default: return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Work From Home</h1>
        <p className="text-gray-600">Apply for WFH and track your requests</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('apply-wfh')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'apply-wfh'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Home className="inline h-4 w-4 mr-2" />
              Apply for WFH
            </button>
            <button
              onClick={() => setActiveTab('my-wfh')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my-wfh'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-2" />
              My WFH Requests
            </button>
          </nav>
        </div>

        <div className="p-4">
          {activeTab === 'my-wfh' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-medium text-gray-900">My WFH Requests</h3>
                  {autoRefreshing && (
                    <span className="text-xs text-gray-500 flex items-center">
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Auto-refreshing...
                    </span>
                  )}
                </div>
                <button
                  onClick={fetchWfhRequests}
                  disabled={listLoading}
                  className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${listLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {listLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : wfhRequests.length > 0 ? (
                <div className="space-y-4">
                  {wfhRequests.map((wfh) => (
                    <div key={wfh._id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <Home className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                            <span className="text-lg font-medium text-gray-900">
                              {new Date(wfh.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              {new Date(wfh.startDate).toDateString() !== new Date(wfh.endDate).toDateString() && (
                                <> to {new Date(wfh.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                              )}
                            </span>
                          </div>

                          {wfh.wfhDays && wfh.wfhDays.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Days:</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {wfh.wfhDays.map((day, index) => (
                                  <div key={index} className="flex items-center space-x-2 text-xs bg-gray-50 rounded px-2 py-1">
                                    <span className="text-gray-600">
                                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded ${day.dayType === 'half' ? 'bg-yellow-100 text-yellow-700' : 'bg-cyan-100 text-cyan-700'}`}>
                                      {day.dayType === 'half' ? 'Half' : 'Full'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="mt-3">
                            <p className="text-sm text-gray-600"><strong>Reason:</strong> {wfh.reason}</p>
                          </div>
                          {wfh.comments && (
                            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2">
                              <p className="text-xs font-semibold text-amber-900">Admin Comments:</p>
                              <p className="text-sm text-amber-800 mt-1">{wfh.comments}</p>
                            </div>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
                            <span>Applied: {new Date(wfh.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>

                        <div className="mt-4 md:mt-0 md:ml-4">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(wfh.status)}`}>
                            {getStatusIcon(wfh.status)}
                            <span className="ml-1">{getStatusText(wfh.status)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Home className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No WFH requests</h3>
                  <p className="mt-1 text-sm text-gray-500">You haven't applied for WFH yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'apply-wfh' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="h-6 w-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">How WFH Application Works</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Select start and end dates for your WFH period</li>
                      <li>• Your request goes to admin for approval</li>
                      <li>• Approved WFH days will show on your attendance calendar</li>
                    </ul>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      End Date
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      min={formData.startDate}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                {wfhDays.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Configure WFH Days ({wfhDays.length} day{wfhDays.length !== 1 ? 's' : ''})
                    </label>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-96 overflow-y-auto">
                      {wfhDays.map((day, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 flex items-center justify-between">
                          <div className="flex items-center">
                            <Calendar className="h-5 w-5 text-gray-500 mr-2" />
                            <div>
                              <p className="text-xs text-gray-500">Date</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={() => handleDayTypeChange(index, 'full')}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                day.dayType === 'full' ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              Full Day
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDayTypeChange(index, 'half')}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                day.dayType === 'half' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              Half Day
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Reason (minimum 10 characters)
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Please provide a reason for working from home..."
                    required
                    minLength="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.reason.length}/10 characters minimum</p>
                </div>

                <button
                  type="submit"
                  disabled={loading || wfhDays.length === 0}
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Home className="h-5 w-5" />
                      <span>Submit WFH Request</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WFHApplication;